<template>
  <VSpinner v-if="isLoading" class="offset-top-2" />
  <article v-else-if="authCallFailed">
    <VServiceDisplay :selected-service="xtcAuthResponse.serviceId" />
    <VNotification type="ERROR" heading="payment.auth_error_headline" class="offset-top-2" inner-class="offset-bottom-1">
      <span class="offset-right-1">{{ errorContent }}</span>
    </VNotification>
    <div class="offset-top-2 pull-right">
      <VButton label="payment.progress_button_new_booking" class="btn-default offset-right-1" @click="onNewBooking" />
      <VButton v-focus label="payment.progress_button_previous_step" class="btn-default btn-brand pull-right" @click="onPreviousStep" />
    </div>
  </article>
  <article v-else>
    <VServiceDisplay :selected-service="serviceId">
      <template v-if="serviceData.effortEnabled && !serviceData.unitsEnabled">
        <VEntry name="payment.timeunits_label" :value="translatedTimeUnits" />
        <VEntry name="payment.preliminary_calculated_units" :value="xtcAuthResponse.units" />
      </template>
      <VEntry v-else name="payment.auth_data_initial_units_label" :value="xtcAuthResponse.units" />
      <VEntry
        v-if="serviceData.serviceDescriptionEnabled"
        name="payment.auth_data_external_description_label"
        :value="formValues.serviceDescription"
        mandatory
      />
      <VEntry v-if="serviceData.commentEnabled" name="payment.auth_data_external_comment_label" :value="formValues.commentField" mandatory />
      <VEntry
        v-for="key in Object.keys(serviceData.descriptions)"
        :key="key"
        :name="'payment.select_description_' + key.toLowerCase()"
        :value="formValues[key]"
      />
      <VEntry name="payment.preliminary_price_headline" :value="preliminaryPrice" />
    </VServiceDisplay>
    <div class="offset-top-2 pull-right">
      <VButton label="payment.progress_button_previous_step" class="btn-default offset-right-1" @click="onPreviousStep" />
      <VButton v-focus label="payment.book_button_label" class="btn-default btn-brand pull-right" @click="onNextStep" />
    </div>
  </article>
</template>

<script>
import VSpinner from '@/components/Spinner';
import VEntry from '@/components/DescriptionList/Entry';
import VButton from '@/components/Button';
import VNotification from '@/components/Notification';
import { PAYMENT_GENERATION, PAYMENT_GENERATION_CONFIRMATION } from '@/router/route-types';
import { mapActions, mapState } from 'vuex';
import VServiceDisplay from '@/components/PaymentGeneration/ServiceDisplay';
import formatCurrency from '@/mixins/formatCurrency';
import focus from '@/directives/focus';

export default {
  name: 'ProductSummary',

  components: {
    VEntry,
    VButton,
    VServiceDisplay,
    VSpinner,
    VNotification,
  },

  directives: { focus },

  mixins: [formatCurrency],

  data() {
    return {
      loading: false,
    };
  },

  computed: {
    ...mapState('payment', ['formValues', 'serviceData', 'serviceId', 'xtcAuthResponse', 'commentField']),

    isLoading() {
      return this.loading || this.xtcAuthResponse === null;
    },

    authCallFailed() {
      return this.xtcAuthResponse?.callStatus === 'ERROR';
    },

    grossAmount() {
      return this.formatCurrency(
        this.xtcAuthResponse.price.grossAmount,
        this.xtcAuthResponse.price.currency,
        -1 * Math.pow(10, this.xtcAuthResponse.price.decimalPlaces)
      );
    },

    netAmount() {
      return this.formatCurrency(
        this.xtcAuthResponse.price.netAmount,
        this.xtcAuthResponse.price.currency,
        -1 * Math.pow(10, this.xtcAuthResponse.price.decimalPlaces)
      );
    },

    preliminaryPrice() {
      return this.$t('payment.preliminary_price_value_format', {
        grossAmount: this.grossAmount,
        netAmount: this.netAmount,
      });
    },

    errorContent() {
      return this.$tf(`payment.${this.xtcAuthResponse?.errorText}`, () => {
        throw this.xtcAuthResponse.error;
      });
    },

    translatedTimeUnits() {
      return this.$t('payment.timeunits_text', {
        hours: this.$tc('payment.timeunits_hours', this.formValues.hours),
        minutes: this.$tc('payment.timeunits_minutes', this.formValues.minutes),
      });
    },
  },

  methods: {
    async onNextStep() {
      this.loading = true;
      await this.callXtcBook();
      this.$router.push({
        name: PAYMENT_GENERATION_CONFIRMATION,
      });
    },

    onPreviousStep() {
      this.$router.back();
    },

    onNewBooking() {
      this.reset();
      this.$router.push({
        name: PAYMENT_GENERATION,
      });
    },

    ...mapActions('payment', ['callXtcBook', 'reset']),
  },
};
</script>
